import React from 'react';

// ─── Time-slot input shape ──────────────────────────────────────────────────
//
// A single proposed/counter-proposed time slot as edited in the UI. The flat
// venueName/venueAddress fields are mapped to the API's `venue: { name, address }`
// at submit time by the caller.

export interface SlotInput {
  date: string;
  time: string;
  venueName: string;
  venueAddress: string;
}

export const emptySlot = (): SlotInput => ({
  date: '',
  time: '',
  venueName: '',
  venueAddress: '',
});

/** Whether every required field of a slot is filled. */
export const isSlotComplete = (s: SlotInput): boolean =>
  !!(s.date && s.time && s.venueName.trim() && s.venueAddress.trim());

interface SlotEditorProps {
  index: number;
  slot: SlotInput;
  onChange: (slot: SlotInput) => void;
  onRemove?: () => void;
  // When true, empty required fields are highlighted (after a submit attempt).
  showErrors?: boolean;
  // Season window (ISO YYYY-MM-DD). A date outside [minDate, maxDate] is flagged.
  minDate?: string;
  maxDate?: string;
}

/** ISO YYYY-MM-DD dates compare correctly as strings. */
export const isDateOutsideSeason = (date: string, minDate?: string, maxDate?: string): boolean =>
  !!date && ((!!minDate && date < minDate) || (!!maxDate && date > maxDate));

/**
 * Shared slot editor used by both the propose-game form and the counter-proposal
 * form so they look and validate identically (date, time, venue and court are
 * all required; empty fields are highlighted after a submit attempt).
 */
const SlotEditor: React.FC<SlotEditorProps> = ({
  index,
  slot,
  onChange,
  onRemove,
  showErrors,
  minDate,
  maxDate,
}) => {
  const update = (field: keyof SlotInput, value: string) => onChange({ ...slot, [field]: value });

  const base =
    'mt-1 w-full rounded-md border px-2 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
  // Red border + tint when a required field is empty after a submit attempt.
  const fieldCls = (value: string) =>
    `${base} ${showErrors && !value.trim() ? 'border-danger-400 bg-danger-50' : 'border-gray-300'}`;
  const reqLabel = 'block text-xs text-gray-500';
  const incomplete = showErrors && !isSlotComplete(slot);
  // A chosen date outside the season window is always flagged (independent of submit).
  const dateOutOfSeason = isDateOutsideSeason(slot.date, minDate, maxDate);
  const dateFieldCls = `${base} ${
    dateOutOfSeason
      ? 'border-danger-500 bg-danger-50'
      : showErrors && !slot.date
      ? 'border-danger-400 bg-danger-50'
      : 'border-gray-300'
  }`;

  return (
    <div
      className={`mt-3 border rounded-md p-3 ${
        incomplete ? 'border-danger-300' : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500">Slot {index + 1}</span>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-gray-400 hover:text-danger-600 transition-colors"
          >
            Remove
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label htmlFor={`slot-date-${index}`} className={reqLabel}>
            Date <span className="text-danger-500">*</span>
          </label>
          <input
            id={`slot-date-${index}`}
            type="date"
            value={slot.date}
            min={minDate}
            max={maxDate}
            onChange={e => update('date', e.target.value)}
            className={dateFieldCls}
            aria-invalid={dateOutOfSeason || undefined}
            required
          />
          {dateOutOfSeason && (
            <p className="mt-1 text-xs text-danger-600">
              Date must be within the season ({minDate} – {maxDate}).
            </p>
          )}
        </div>
        <div>
          <label htmlFor={`slot-time-${index}`} className={reqLabel}>
            Time <span className="text-danger-500">*</span>
          </label>
          <input
            id={`slot-time-${index}`}
            type="time"
            value={slot.time}
            onChange={e => update('time', e.target.value)}
            className={fieldCls(slot.time)}
            required
          />
        </div>
        <div>
          <label htmlFor={`slot-venue-${index}`} className={reqLabel}>
            Venue <span className="text-danger-500">*</span>
          </label>
          <input
            id={`slot-venue-${index}`}
            type="text"
            value={slot.venueName}
            onChange={e => update('venueName', e.target.value)}
            placeholder="Sports Hall"
            className={fieldCls(slot.venueName)}
            required
          />
        </div>
        <div>
          <label htmlFor={`slot-address-${index}`} className={reqLabel}>
            Court / address <span className="text-danger-500">*</span>
          </label>
          <input
            id={`slot-address-${index}`}
            type="text"
            value={slot.venueAddress}
            onChange={e => update('venueAddress', e.target.value)}
            placeholder="Court 1"
            className={fieldCls(slot.venueAddress)}
            required
          />
        </div>
      </div>
      {incomplete && (
        <p className="mt-2 text-xs text-danger-600">
          Fill in every required field (highlighted) to complete this slot.
        </p>
      )}
    </div>
  );
};

export default SlotEditor;
